/* eslint-disable no-irregular-whitespace */
// this method uses narrow no-break space
// https://blanktext.net/narrow-no-break-space-202F/

import i18next from 'i18next';

import {
  isLinkAssetLink,
  isLinkExternalLink,
  isLinkInternalLink,
  LinkActionLink,
  LinkAssetLink,
  LinkExternalLink,
  LinkInternalLink,
  LinkThematischerLink,
} from '../../models';

// eslint-disable-next-line import/no-named-as-default-member
const t = i18next.getFixedT.bind(i18next)(null, 'root', 'link');

export const convertSizeToDisplayValue = (fileSizeInKB: number) => {
  const narrowNoBreakSpace = ' ';

  if (fileSizeInKB < 1) {
    return `${Math.round(fileSizeInKB * 1000)}${narrowNoBreakSpace}B`;
  }
  if (fileSizeInKB < 10) {
    return `${fileSizeInKB.toFixed(1).replace('.', ',')}${narrowNoBreakSpace}KB`;
  }
  if (fileSizeInKB < 1000) {
    return `${Math.round(fileSizeInKB)}${narrowNoBreakSpace}KB`;
  }

  const fileSizeInMB = fileSizeInKB / 1000;
  if (fileSizeInMB < 10) {
    return `${fileSizeInMB.toFixed(1).replace('.', ',')}${narrowNoBreakSpace}MB`;
  }
  if (fileSizeInMB < 1000) {
    return `${Math.round(fileSizeInMB)}${narrowNoBreakSpace}MB`;
  }

  const fileSizeInGB = fileSizeInMB / 1000;
  return fileSizeInGB < 10
    ? `${fileSizeInGB.toFixed(1).replace('.', ',')}${narrowNoBreakSpace}GB`
    : `${Math.round(fileSizeInGB)}${narrowNoBreakSpace}GB`;
};

export const getFileTypeFromMime = (mimeType: string): string => {
  if (!mimeType) {
    return '';
  }

  const normalizedMime = mimeType.toLowerCase().trim();

  switch (normalizedMime) {
    // PDF
    case 'application/pdf':
    case 'application/x-pdf':
    case 'application/acrobat':
    case 'application/vnd.pdf':
    case 'text/pdf':
    case 'text/x-pdf':
      return 'PDF';

    // PNG
    case 'image/png':
    case 'image/x-png':
    case 'application/png':
    case 'application/x-png':
      return 'PNG';

    // JPEG
    case 'image/jpeg':
    case 'image/jpg':
    case 'image/pjpeg':
    case 'image/pipeg':
    case 'image/x-jpeg':
    case 'image/x-jpg':
    case 'application/jpg':
    case 'application/x-jpg':
    case 'application/jpeg':
    case 'application/x-jpeg':
      return 'JPEG';

    // GIF
    case 'image/gif':
    case 'image/x-gif':
    case 'application/gif':
    case 'application/x-gif':
      return 'GIF';

    // WEBP
    case 'image/webp':
    case 'image/x-webp':
    case 'application/webp':
    case 'application/x-webp':
      return 'WEBP';

    // DOC
    case 'application/msword':
    case 'application/doc':
    case 'application/vnd.ms-word':
    case 'application/vnd.msword':
    case 'application/winword':
    case 'application/word':
    case 'application/x-msw6':
    case 'application/x-msword':
    case 'application/x-doc':
      return 'DOC';

    // DOCX
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'application/vnd.ms-word.document.macroEnabled.12':
    case 'application/vnd.ms-word.document.12':
      return 'DOCX';

    default:
      return '';
  }
};

export const getAriaLabelDescriptionFromLink = (
  link: LinkAssetLink | LinkExternalLink | LinkInternalLink | LinkThematischerLink | LinkActionLink
) => {
  if (isLinkAssetLink(link) && getFileTypeFromMime(link.media.mime) === 'PDF') {
    return t('ariaLabel.assetLinkPdf');
  }
  if (isLinkAssetLink(link)) {
    return t('ariaLabel.assetLink');
  }

  if (isLinkExternalLink(link) && link.url.startsWith('tel:')) {
    return t('ariaLabel.externalLinkPhone');
  }

  if (isLinkExternalLink(link) && link.url.startsWith('mailto:')) {
    return t('ariaLabel.externalLinkEmail');
  }

  if (isLinkExternalLink(link)) {
    return t('ariaLabel.externalLink');
  }

  if (isLinkInternalLink(link)) {
    return t('ariaLabel.internalLink');
  }

  return '';
};
